import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { CoinIcon } from '../components/icons/NavIcons';
import LoadingSpinner from '../components/LoadingSpinner';

/**
 * Composant d'autocomplétion d'adresse avec Nominatim
 */
const AddressAutocomplete = ({ value, onChange, required = false }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(value?.formatted_address || '');
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (value?.formatted_address) {
      setSelectedAddress(value.formatted_address);
      setQuery('');
    }
  }, [value]);

  const searchAddress = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        new URLSearchParams({
          q: searchQuery,
          format: 'json',
          addressdetails: '1',
          limit: '5',
          countrycodes: 'fr'
        }),
        {
          headers: {
            'Accept-Language': 'fr'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Erreur lors de la recherche d\'adresse:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    setSelectedAddress('');
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      searchAddress(newValue);
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const selectAddress = (address) => {
    const streetNumber = address.address?.house_number || '';
    const streetName = address.address?.road || '';
    const formattedStreetAddress = `${streetNumber} ${streetName}`.trim();
    
    const addressData = {
      formatted_address: formattedStreetAddress,
      street_number: streetNumber,
      street_name: streetName,
      postal_code: address.address?.postcode || '',
      city: address.address?.city || address.address?.town || address.address?.village || '',
      region: address.address?.state || '',
      country: address.address?.country || 'France',
      latitude: parseFloat(address.lat),
      longitude: parseFloat(address.lon)
    };

    setSelectedAddress(address.display_name);
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    onChange(addressData);
  };

  const clearAddress = () => {
    setSelectedAddress('');
    setQuery('');
    setSuggestions([]);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    onChange({
      formatted_address: '',
      street_number: '',
      street_name: '',
      postal_code: '',
      city: '',
      region: '',
      country: ''
    });
  };

  return (
    <div className="relative">
      {selectedAddress ? (
        <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-300 rounded-lg">
          <span className="flex-1 text-sm text-gray-700">{selectedAddress}</span>
          <button
            type="button"
            onClick={clearAddress}
            className="text-red-500 hover:text-red-700 text-sm font-medium"
          >
            ✕
          </button>
        </div>
      ) : (
        <>
          <input
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => query && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            required={required}
            placeholder={t('createEvent.searchAddress') || 'Rechercher une adresse...'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
          />
          {loading && (
            <div className="absolute right-3 top-3">
              <div className="animate-spin h-5 w-5 border-2 border-bleu1 border-t-transparent rounded-full"></div>
            </div>
          )}
        </>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              onClick={() => selectAddress(suggestion)}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
            >
              <div className="text-sm text-gray-900">{suggestion.display_name}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Formulaire d'édition d'événement
 */
const EditEventForm = ({ event, currentUser, userRole, onSuccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [formData, setFormData] = useState({
    title: event.title || '',
    description: event.description || '',
    category_id: event.category_id || '',
    age_group_id: event.age_group_id || '',
    is_paid: event.is_paid || false,
    external_url: event.external_url || '',
    is_accessible: event.is_accessible || false,
    weather_dependent: event.weather_dependent || false,
    boost: event.boost || false,
    location_name: event.location_name || '',
    formatted_address: event.formatted_address || '',
    street_number: event.street_number || '',
    street_name: event.street_name || '',
    postal_code: event.postal_code || '',
    city: event.city || '',
    region: event.region || '',
    country: event.country || '',
    latitude: event.latitude || null,
    longitude: event.longitude || null,
    start_date: event.start_date || '',
    end_date: event.end_date || '',
    start_time: event.start_time || '',
    end_time: event.end_time || ''
  });

  // Charger les catégories et groupes d'âge
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes, ageGroupsRes] = await Promise.all([
          supabase.from('categories').select('*').order('name'),
          supabase.from('age_groups').select('*').order('id')
        ]);

        if (categoriesRes.data) setCategories(categoriesRes.data);
        if (ageGroupsRes.data) setAgeGroups(ageGroupsRes.data);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      }
    };

    fetchData();
  }, []);

  // Calculer automatiquement la date de fin à partir de la date de début
  useEffect(() => {
    if (formData.start_date && !formData.end_date) {
      setFormData(prev => ({
        ...prev,
        end_date: prev.start_date
      }));
    }
  }, [formData.start_date]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'start_date') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        end_date: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleAddressChange = (addressData) => {
    setFormData(prev => ({
      ...prev,
      ...addressData
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Vérifier que nous avons une adresse avec coordonnées
      if (!formData.latitude || !formData.longitude) {
        alert(t('common.error') + ': ' + t('event.location'));
        setLoading(false);
        return;
      }

      // Déterminer le statut selon le rôle de l'utilisateur
      const eventStatus = userRole === 'admin' ? 'published' : 'pending_approval';

      // Préparer les données de l'événement
      const eventData = {
        title: formData.title,
        description: formData.description,
        category_id: formData.category_id,
        age_group_id: formData.age_group_id || null,
        is_paid: formData.is_paid,
        external_url: formData.external_url || null,
        is_accessible: formData.is_accessible,
        weather_dependent: formData.weather_dependent,
        boost: formData.boost,
        location_name: formData.location_name,
        formatted_address: formData.formatted_address,
        street_number: formData.street_number || null,
        street_name: formData.street_name || null,
        postal_code: formData.postal_code,
        city: formData.city,
        region: formData.region || null,
        country: formData.country || 'France',
        latitude: formData.latitude,
        longitude: formData.longitude,
        start_date: formData.start_date,
        end_date: formData.end_date,
        start_time: formData.start_time || null,
        end_time: formData.end_time || null,
        timezone: 'Europe/Paris',
        status: eventStatus
      };

      // Mettre à jour l'événement
      const { error: updateError } = await supabase
        .from('events')
        .update(eventData)
        .eq('id', event.id);

      if (updateError) {
        console.error('Erreur lors de la mise à jour de l\'événement:', updateError);
        throw updateError;
      }

      // Marquer tous les signalements liés à cet événement comme résolus
      const { error: reportsError } = await supabase
        .from('reports')
        .update({ status: 'resolved' })
        .eq('event_id', event.id);

      if (reportsError) {
        console.error('Erreur lors de la mise à jour des signalements:', reportsError);
        // On ne bloque pas le processus si cette étape échoue
      }

      console.log('Événement mis à jour avec succès:', event.id, 'Statut:', eventStatus);

      // Rediriger vers My Events avec l'onglet myEvents activé
      navigate('/my-events', { state: { activeTab: 'myEvents' } });
      
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert(t('common.error') + ': ' + (error.message || t('errors.unknownError')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6 mb-20">
      {/* Message de brouillon */}
      {event.message && (
        <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded">
          <div className="flex items-start">
            <span className="text-orange-600 text-xl mr-3">💬</span>
            <div className="flex-1">
              <p className="text-sm font-medium text-orange-800 mb-1">{t('editEvent.moderatorMessage')}</p>
              <p className="text-sm text-orange-700">{event.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Informations de base */}
      <div className="space-y-4">
        <h3 className="text-h3 text-bleu2">{t('event.details')}</h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('event.title')} *
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            maxLength={100}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('event.description')} *
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            maxLength={400}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">
            {formData.description.length}/400
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('filters.categories')} *
            </label>
            <select
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
            >
              <option value="">{t('filters.allCategories')}</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {t(`categories.${cat.name}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('event.ageGroup')}
            </label>
            <select
              name="age_group_id"
              value={formData.age_group_id}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
            >
              <option value="">{t('filters.allAgeGroups')}</option>
              {ageGroups.map(group => (
                <option key={group.id} value={group.id}>
                  {t(`ageGroups.${group.name}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('event.externalURL')}
          </label>
          <input
            type="url"
            name="external_url"
            value={formData.external_url}
            onChange={handleChange}
            maxLength={255}
            placeholder="https://..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
          />
        </div>
      </div>

      {/* Dates et heures */}
      <div className="space-y-4">
        <h3 className="text-h3 text-bleu2">{t('event.date')} & {t('event.time')}</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('filters.startDate')} *
            </label>
            <input
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              {t('filters.endDate')}
            </label>
            <input
              type="date"
              name="end_date"
              value={formData.end_date}
              readOnly
              disabled
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('event.time')} ({t('filters.startDate')})
            </label>
            <input
              type="time"
              name="start_time"
              value={formData.start_time}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('event.time')} ({t('filters.endDate')})
            </label>
            <input
              type="time"
              name="end_time"
              value={formData.end_time}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Localisation */}
      <div className="space-y-4">
        <h3 className="text-h3 text-bleu2">{t('event.location')}</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('event.address')} *
          </label>
          <AddressAutocomplete
            value={formData}
            onChange={handleAddressChange}
            required
          />
        </div>
        {formData.formatted_address && (
          <div className="p-4 bg-gray-50 rounded-lg space-y-2 text-sm text-gray-600">
            <p><span className="font-medium">Adresse :</span> {formData.street_number} {formData.street_name}</p>
            <p><span className="font-medium">Code postal :</span> {formData.postal_code}</p>
            <p><span className="font-medium">Ville :</span> {formData.city}</p>
            {formData.region && <p><span className="font-medium">Région :</span> {formData.region}</p>}
            <p><span className="font-medium">Pays :</span> {formData.country}</p>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('event.namelocation')} *
          </label>
          <input
            type="text"
            name="location_name"
            value={formData.location_name}
            onChange={handleChange}
            required
            maxLength={255}
            placeholder={t('createEvent.locationNamePlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bleu1 focus:border-transparent"
          />
        </div>
      </div>

      {/* Options */}
      <div className="space-y-4">
        <h3 className="text-h3 text-bleu2">Options</h3>
        
        <div className="space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="is_paid"
              checked={formData.is_paid}
              onChange={handleChange}
              className="w-4 h-4 text-bleu1 border-gray-300 rounded focus:ring-bleu1 accent-bleu1"
            />
            <span className="text-sm text-gray-700">{t('event.paid')}</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="is_accessible"
              checked={formData.is_accessible}
              onChange={handleChange}
              className="w-4 h-4 text-bleu1 border-gray-300 rounded focus:ring-bleu1 accent-bleu1"
            />
            <span className="text-sm text-gray-700">{t('event.accessible')}</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="weather_dependent"
              checked={formData.weather_dependent}
              onChange={handleChange}
              className="w-4 h-4 text-bleu1 border-gray-300 rounded focus:ring-bleu1 accent-bleu1"
            />
            <span className="text-sm text-gray-700">{t('event.weatherDependent')}</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="boost"
              checked={formData.boost}
              onChange={handleChange}
              className="w-4 h-4 text-bleu1 border-gray-300 rounded focus:ring-bleu1 accent-bleu1"
            />
            <span className="text-sm text-gray-700">{t('eventList.featured')}</span>
          </label>
        </div>
      </div>

      {/* Boutons */}
      <div className="flex gap-4 pt-4 border-t">
        <button
          type="button"
          onClick={() => navigate('/my-events', { state: { activeTab: 'myEvents' } })}
          className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 px-6 py-3 bg-bleu1 text-white rounded-lg hover:bg-bleu3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? t('common.loading') : t('common.save')}
        </button>
      </div>
    </form>
  );
};

/**
 * Page d'édition d'événement
 */
const EditEventPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);

  // Récupérer l'utilisateur connecté
  useEffect(() => {
    const getUser = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error) {
          console.error("Erreur lors de la récupération de l'utilisateur:", error);
          setCurrentUser(null);
        } else {
          setCurrentUser(user);
        }
      } catch (err) {
        console.error("Exception lors de la vérification de l'authentification:", err);
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };
    
    getUser();
  }, []);

  // Récupérer l'événement
  useEffect(() => {
    const fetchEvent = async () => {
      if (!currentUser || !eventId) return;

      try {
        // Récupérer le profil de l'utilisateur pour vérifier le rôle
        const { data: roleData, error: roleError } = await supabase
          .from('roles')
          .select('role')
          .eq('id', currentUser.id)
          .single();

        if (roleError) {
          console.error("Erreur lors de la récupération du rôle:", roleError);
        }

        // Stocker le rôle de l'utilisateur
        setUserRole(roleData?.role);

        const { data, error } = await supabase
          .from('events')
          .select('*')
          .eq('id', eventId)
          .single();

        if (error) {
          console.error("Erreur lors de la récupération de l'événement:", error);
          navigate('/my-events');
          return;
        }

        // Vérifier que l'utilisateur est bien le créateur OU un admin
        const isOwner = data.organizer_id === currentUser.id;
        const isAdmin = roleData?.role === 'admin';
        
        if (!isOwner && !isAdmin) {
          console.error("Accès non autorisé");
          navigate('/my-events');
          return;
        }

        // Vérifier que l'événement est bien en mode brouillon
        if (data.status !== 'draft') {
          console.error("L'événement n'est pas en mode brouillon");
          navigate(`/event/${eventId}`);
          return;
        }

        setEvent(data);
      } catch (err) {
        console.error("Exception lors de la récupération de l'événement:", err);
        navigate('/my-events');
      }
    };

    fetchEvent();
  }, [currentUser, eventId, navigate]);

  if (loading || !event) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-80px)]">
        <LoadingSpinner />
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 px-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-h1 text-bleu2 mb-6">
            {t('editEvent.title')}
          </h1>
          
          <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)] p-4">
            <div className="text-6xl mb-4">👋</div>
            <h2 className="text-h1 text-center mb-4">{t('myEvents.loginRequired')}</h2>
            <p className="text-body text-gray-600 text-center mb-6">{t('createEvent.loginDescription')}</p>
            <button 
              onClick={() => navigate('/settings')}
              className="bg-[#2894D8] text-white py-3 px-6 rounded-lg font-medium hover:bg-[#136E9D] transition-colors"
            >
              {t('common.login')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-4">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-h1 text-bleu2 mb-6">
          {t('editEvent.title')}
        </h1>
        
        {/* Formulaire d'édition d'événement */}
        <EditEventForm 
          event={event}
          currentUser={currentUser}
          userRole={userRole}
        />
      </div>
    </div>
  );
};

export default EditEventPage;
